import { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const THEMES = {
  light: {
    name: "light",
    bg:          "#FAFAFA",
    bgSecond:    "#FFFFFF",
    bgCard:      "#FFFFFF",
    bgInput:     "#F5F5F4",
    bgHover:     "#F0F0EE",
    bgActive:    "#FFF4F0",
    border:      "#E8E8E5",
    borderLight: "#F0F0EE",
    text:        "#1A1A1A",
    textSub:     "#3D3D3D",
    textMuted:   "#9B9B9B",
    textLight:   "#C4C4C0",
    orange:      "#E8520A",
    orangeLight: "#FFF4F0",
    orangeBorder:"#FDDDD0",
    shadow:      "0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04)",
    shadowMd:    "0 4px 12px rgba(0,0,0,.08), 0 2px 4px rgba(0,0,0,.04)",
    shadowLg:    "0 16px 40px rgba(0,0,0,.12), 0 4px 12px rgba(0,0,0,.06)",
  },
  dark: {
    name: "dark",
    bg:          "#111111",
    bgSecond:    "#1A1A1A",
    bgCard:      "#1E1E1E",
    bgInput:     "#252525",
    bgHover:     "#2A2A2A",
    bgActive:    "#2D1F18",
    border:      "#2E2E2E",
    borderLight: "#262626",
    text:        "#F0F0F0",
    textSub:     "#C8C8C8",
    textMuted:   "#888888",
    textLight:   "#555555",
    orange:      "#FF6B2B",
    orangeLight: "#2D1F18",
    orangeBorder:"#4A2A18",
    shadow:      "0 1px 3px rgba(0,0,0,.3)",
    shadowMd:    "0 4px 12px rgba(0,0,0,.4)",
    shadowLg:    "0 16px 40px rgba(0,0,0,.6)",
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("crm_theme");
    return saved === "dark" ? THEMES.dark : THEMES.light;
  });

  // Aplicar classe no body para CSS global
  useEffect(() => {
    document.body.setAttribute("data-theme", theme.name);
  }, [theme]);

  const toggleTheme = () => {
    const next = theme.name === "dark" ? THEMES.light : THEMES.dark;
    setTheme(next);
    localStorage.setItem("crm_theme", next.name);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { font-size: 15px; }

        body {
          background: ${theme.bg};
          font-family: 'Inter', -apple-system, sans-serif;
          color: ${theme.text};
          -webkit-font-smoothing: antialiased;
          line-height: 1.5;
        }

        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${theme.border}; border-radius: 99px; }

        input, select, textarea, button {
          font-family: 'Inter', -apple-system, sans-serif;
          color-scheme: ${theme.name};
        }

        * { transition: background-color .15s ease, border-color .15s ease, color .1s ease; }
        ::selection { background: ${theme.orange}22; color: ${theme.orange}; }

        /* ── DARK MODE — sobrescreve cores hardcoded das páginas ── */
        :root {
          --page-bg: #FAFAFA;
          --card-bg: #FFFFFF;
          --input-bg: #F5F5F4;
          --hover-bg: #F5F6F7;
          --active-bg: #FDEEE3;
          --row-bg: #FFFFFF;
          --border-color: #E3E6E9;
          --border-light: #ECEEF2;
          --text-primary: #16181C;
          --text-secondary: #6B7280;
          --text-muted: #98A2B3;
          --text-light: #C4C4C0;
          --orange: #EE6A1A;
          --orange-light: #FDEEE3;
          --orange-border: #FDDDD0;
        }

        [data-theme="dark"] {
          /* Fundos */
          --page-bg: #111111;
          --card-bg: #1E1E1E;
          --input-bg: #252525;
          --hover-bg: #2A2A2A;
          --row-bg: #1E1E1E;

          /* Textos */
          --text-primary: #F0F0F0;
          --text-secondary: #C8C8C8;
          --text-muted: #888888;

          /* Bordas */
          --border-color: #2E2E2E;
          --border-light: #262626;
          --active-bg: #2D1F18;
          --text-light: #555555;
          --orange: #FF6B2B;
          --orange-light: #2D1F18;
          --orange-border: #4A2A18;
        }

        /* Páginas com fundo fixo */
        [data-theme="dark"] [style*="background:#f6f7fb"],
        [data-theme="dark"] [style*="background: #f6f7fb"] {
          background: #111111 !important;
        }

        [data-theme="dark"] [style*="background:#fff"],
        [data-theme="dark"] [style*='background: #fff'],
        [data-theme="dark"] [style*="background: rgb(255, 255, 255)"] {
          background: #1E1E1E !important;
        }

        [data-theme="dark"] [style*="background:#f5f6f8"],
        [data-theme="dark"] [style*="background: #f5f6f8"],
        [data-theme="dark"] [style*="background:#fafbfc"],
        [data-theme="dark"] [style*="background:#F5F5F4"],
        [data-theme="dark"] [style*="background:#F7F8FA"] {
          background: #252525 !important;
        }

        /* Bordas */
        [data-theme="dark"] [style*="border:1px solid #e6e9ef"],
        [data-theme="dark"] [style*="border: 1px solid #e6e9ef"],
        [data-theme="dark"] [style*="borderBottom:1px solid #e6e9ef"],
        [data-theme="dark"] [style*="border:1px solid #ECEEF2"],
        [data-theme="dark"] [style*="border: 1px solid #ECEEF2"] {
          border-color: #2E2E2E !important;
        }

        /* Textos */
        [data-theme="dark"] [style*="color:#323338"],
        [data-theme="dark"] [style*="color: #323338"],
        [data-theme="dark"] [style*="color:#1A1D23"],
        [data-theme="dark"] [style*="color: #1A1D23"],
        [data-theme="dark"] [style*="color:#1A1A1A"] {
          color: #F0F0F0 !important;
        }

        [data-theme="dark"] [style*="color:#676879"],
        [data-theme="dark"] [style*="color: #676879"],
        [data-theme="dark"] [style*="color:#9499A5"],
        [data-theme="dark"] [style*="color: #9499A5"] {
          color: #888888 !important;
        }

        /* Tabelas */
        [data-theme="dark"] th {
          background: #252525 !important;
          color: #888888 !important;
          border-color: #2E2E2E !important;
        }

        [data-theme="dark"] td {
          border-color: #2E2E2E !important;
        }

        [data-theme="dark"] tr:hover td {
          background: #2A2A2A !important;
        }

        /* Inputs */
        [data-theme="dark"] input,
        [data-theme="dark"] select,
        [data-theme="dark"] textarea {
          background: #252525 !important;
          color: #F0F0F0 !important;
          border-color: #2E2E2E !important;
        }

        /* Header das páginas */
        [data-theme="dark"] [style*="background:#FFFFFF"],
        [data-theme="dark"] [style*="background: #FFFFFF"] {
          background: #1A1A1A !important;
        }

        /* Linhas de tabela */
        [data-theme="dark"] tr {
          background: #1E1E1E !important;
        }

        [data-theme="dark"] tbody tr:hover {
          background: #2A2A2A !important;
        }

        /* Headers de grupos coloridos */
        [data-theme="dark"] [style*="background:#fff"] {
          background: #1E1E1E !important;
        }

        /* Pipeline cards no topo */
        [data-theme="dark"] [style*="borderTop:"] {
          background: #1E1E1E !important;
        }

        /* Modais */
        [data-theme="dark"] [style*="background:"#fff""],
        [data-theme="dark"] [style*='background:"#fff"'] {
          background: #1E1E1E !important;
        }

        /* Textos brancos - garantir contraste */
        [data-theme="dark"] p,
        [data-theme="dark"] span:not([style*="color:#"]):not([style*="color: #"]),
        [data-theme="dark"] div:not([style*="color:#"]):not([style*="color: #"]) {
          color: inherit;
        }

        /* Corrigir fundo branco restante */
        [data-theme="dark"] [style*="background: white"],
        [data-theme="dark"] [style*="background:white"] {
          background: #1E1E1E !important;
        }


        /* Pipeline cards topo - border-top colorido */
        [data-theme="dark"] [style*="boxShadow:"][style*="borderTop:3px"] {
          background: #1E1E1E !important;
          border-color: #2E2E2E !important;
        }

        /* Espaço entre grupos */
        [data-theme="dark"] td[style*="height:16"],
        [data-theme="dark"] td[style*="height:18"] {
          background: #111111 !important;
        }

        /* Separador entre grupos branco */
        [data-theme="dark"] [style*="background:#f6f7fb"] {
          background: #111111 !important;
        }

        /* Cards com border-top colorido (pipeline) */
        [data-theme="dark"] div[style*="border:1px solid #e6e9ef"][style*="borderRadius:8"] {
          background: #1E1E1E !important;
          border-color: #2E2E2E !important;
          color: #F0F0F0 !important;
        }

        /* Textos dentro dos cards pipeline */
        [data-theme="dark"] div[style*="fontSize:10"][style*="textTransform:uppercase"] {
          color: inherit !important;
        }

        [data-theme="dark"] div[style*="fontSize:22"][style*="fontWeight:800"] {
          color: #F0F0F0 !important;
        }

        /* Fundo branco residual */
        [data-theme="dark"] [style*="background:#fafbfc"] {
          background: #252525 !important;
        }

        /* Header das páginas (toolbar) */
        [data-theme="dark"] [style*="borderBottom:1px solid #e6e9ef"][style*="padding:"] {
          background: #1A1A1A !important;
        }
        /* Sidebar footer */
        [data-theme="dark"] [style*="background:#F7F8FA"],
        [data-theme="dark"] [style*="background: #F7F8FA"] {
          background: #252525 !important;
        }

        [data-theme="dark"] [style*="background:#ECEEF2"],
        [data-theme="dark"] [style*="background: #ECEEF2"] {
          background: #2A2A2A !important;
        }
      `}</style>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
